/**
 * ProCalculatorHeader Component
 *
 * Displays the page title and description for the Professional Calculator
 */

import { Title, Text, Stack, Group, Button, Alert, Card, SimpleGrid } from '@mantine/core';
import { IconInfoCircle, IconLock, IconBolt, IconChartDots, IconChartBarPopular, IconChartLine } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

interface ProCalculatorHeaderProps {
  isAuthenticated: boolean;
  showInfoAlert?: boolean;
}

/**
 * Header for authenticated users
 */
function AuthenticatedHeader({ showInfoAlert = true }: { showInfoAlert?: boolean }) {
  return (
    <>
      <div>
        <Title order={1} mb="sm">
          Kalkulator Profesjonalny
        </Title>
        <Text size="lg" c="dimmed">
          Wprowadź dokładne kwoty z faktur i porównaj taryfy
        </Text>
      </div>

      {showInfoAlert && (
        <Alert icon={<IconInfoCircle size={20} />} color="blue" variant="light">
          <Text size="sm">
            Ten kalkulator pozwala na precyzyjną analizę kosztów energii w oparciu o dane z faktur.
            Wprowadź miesięczne zużycie i wybierz wzorzec godzinowy dla każdego miesiąca.
          </Text>
        </Alert>
      )}
    </>
  );
}

/**
 * Header for unauthenticated users (with login CTA)
 */
function UnauthenticatedHeader() {
  const navigate = useNavigate();

  return (
    <>
      {/* Hero Section */}
      <div style={{ backgroundColor: 'var(--mantine-color-ecoGreen-0)', padding: '3rem 2rem', borderRadius: '8px' }}>
        <Stack gap="xl" align="center" ta="center">
          <IconLock size={48} color="var(--mantine-color-ecoGreen-6)" />

          <Title order={1} size={36} fw={800}>
            Kalkulator Profesjonalny{' '}
            <span style={{ color: 'var(--mantine-color-ecoGreen-6)' }}>
              tylko dla zalogowanych
            </span>
          </Title>

          <Text size="xl" c="dimmed" maw={700}>
            Zaloguj się lub załóż darmowe konto, aby uzyskać dostęp do zaawansowanych funkcji
            kalkulatora zaawansowanego i znaleźć najtańszą taryfę dla Twojego gospodarstwa.
          </Text>

          <Group gap="md">
            <Button
              size="xl"
              leftSection={<IconBolt size={24} />}
              onClick={() => navigate('/login')}
            >
              Zaloguj się
            </Button>
            <Button
              size="xl"
              variant="outline"
              onClick={() => navigate('/signup')}
            >
              Załóż konto
            </Button>
          </Group>
        </Stack>
      </div>

      {/* Features Section */}
      <div style={{ marginTop: '3rem' }}>
        <Title order={2} ta="center" mb={40}>
          Co oferuje Kalkulator PRO?
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Card.Section p="xl" ta="center">
              <IconChartDots size={48} color="var(--mantine-color-ecoGreen-6)" />
            </Card.Section>
            <Title order={3} ta="center" mb="md">
              Szczegółowa analiza miesięczna
            </Title>
            <Text c="dimmed" ta="center">
              Oblicz dokładne koszty energii dla każdego miesiąca osobno z uwzględnieniem
              zmiennego zużycia. Wprowadź rzeczywiste kwoty z faktur i uzyskaj precyzyjne
              porównanie wszystkich dostępnych taryf.
            </Text>
          </Card>

          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Card.Section p="xl" ta="center">
              <IconChartBarPopular size={48} color="var(--mantine-color-ecoGreen-6)" />
            </Card.Section>
            <Title order={3} ta="center" mb="md">
              Precyzyjne porównanie taryf
            </Title>
            <Text c="dimmed" ta="center">
              Symulacja wszystkich 6 taryf (G11, G12, G12w, G12as, G13, G13s) na podstawie
              Twoich rzeczywistych danych. Zobacz dokładne koszty i wybierz najlepszą opcję
              dla Twojego gospodarstwa.
            </Text>
          </Card>

          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Card.Section p="xl" ta="center">
              <IconBolt size={48} color="var(--mantine-color-ecoGreen-6)" />
            </Card.Section>
            <Title order={3} ta="center" mb="md">
              Rekomendacja najtańszej taryfy
            </Title>
            <Text c="dimmed" ta="center">
              System automatycznie wskaże, która taryfa będzie dla Ciebie najbardziej opłacalna
              i ile możesz zaoszczędzić w skali roku. Oszczędności mogą sięgać nawet kilkuset
              złotych rocznie!
            </Text>
          </Card>

          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Card.Section p="xl" ta="center">
              <IconChartLine size={48} color="var(--mantine-color-ecoGreen-6)" />
            </Card.Section>
            <Title order={3} ta="center" mb="md">
              Wzorce godzinowe
            </Title>
            <Text c="dimmed" ta="center">
              Dostosuj profil zużycia energii do Twojego stylu życia (praca z domu, samochód
              elektryczny, pompa ciepła). Wybierz gotowy wzorzec lub skonfiguruj własny,
              aby uzyskać najbardziej dokładne wyniki.
            </Text>
          </Card>
        </SimpleGrid>
      </div>
    </>
  );
}

/**
 * Main Header Component
 */
export function ProCalculatorHeader({ isAuthenticated, showInfoAlert = true }: ProCalculatorHeaderProps) {
  if (isAuthenticated) {
    return <AuthenticatedHeader showInfoAlert={showInfoAlert} />;
  }

  return <UnauthenticatedHeader />;
}
